// Structured PDF export for the V2 Work Summary.
// Renders logo header + KPI strip + activity breakdown + session timeline +
// highlights + insights from the { report, metrics } objects. Uses jsPDF (pt/A4).

import jsPDF from "jspdf";

const INK = { text: [15, 18, 34], sub: [83, 93, 114], faint: [138, 147, 168], line: [225, 228, 238] };
const BRAND = [91, 91, 214];
const BRAND2 = [124, 58, 237];
const GREEN = [22, 163, 74];
const AMBER = [217, 119, 6];
const SKY = [14, 165, 233];

const CAT_COLOR = {
  Development: [91, 91, 214], Review: [139, 92, 246], Communication: [14, 165, 233],
  Research: [20, 184, 166], Testing: [245, 158, 11], Planning: [236, 72, 153], Documentation: [16, 185, 129],
};
const catColor = (name) => CAT_COLOR[name] || [100, 116, 139];

// Rasterize the SVG logo (from /public) to a PNG data URL for crisp jsPDF placement.
async function loadLogoPng(width = 320) {
  try {
    // Full-color TeamGrid brand logo (navy wordmark + colored icon) → placed on a
    // white plate over the header band. Falls back to logo-dark if unavailable.
    let res = await fetch("/logo-light.svg");
    if (!res.ok) res = await fetch("/logo-dark.svg");
    if (!res.ok) return null;
    let svg = await res.text();
    const vb = svg.match(/viewBox="0 0 ([\d.]+) ([\d.]+)"/);
    const ratio = vb ? Number(vb[2]) / Number(vb[1]) : 654 / 2763;
    const height = Math.round(width * ratio);
    // ensure explicit pixel size so the raster is sharp
    svg = svg.replace(/<svg /, `<svg width="${width}" height="${height}" `);
    const blob = new Blob([svg], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const img = await new Promise((resolve, reject) => {
      const im = new Image();
      im.onload = () => resolve(im);
      im.onerror = reject;
      im.src = url;
    });
    const scale = 3; // hi-dpi
    const canvas = document.createElement("canvas");
    canvas.width = width * scale;
    canvas.height = height * scale;
    const ctx = canvas.getContext("2d");
    ctx.scale(scale, scale);
    ctx.drawImage(img, 0, 0, width, height);
    URL.revokeObjectURL(url);
    return { dataUrl: canvas.toDataURL("image/png"), width, height };
  } catch (_) {
    return null;
  }
}

const fmtHours = (h) => {
  const H = Math.floor(h || 0), M = Math.round(((h || 0) - H) * 60);
  return H > 0 && M > 0 ? `${H}h ${M}m` : H > 0 ? `${H}h` : `${M}m`;
};

export async function downloadWorkSummaryPDF({ memberName, position, dateLabel, report, metrics }) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const PW = doc.internal.pageSize.getWidth();
  const PH = doc.internal.pageSize.getHeight();
  const M = 44;
  const CW = PW - M * 2;
  let y = M;

  const logo = await loadLogoPng(300);

  const pageBreak = (need) => {
    if (y + need > PH - 56) { footer(); doc.addPage(); y = M; return true; }
    return false;
  };
  const setFill = (c) => doc.setFillColor(c[0], c[1], c[2]);
  const setText = (c) => doc.setTextColor(c[0], c[1], c[2]);
  const setDraw = (c) => doc.setDrawColor(c[0], c[1], c[2]);

  // ── Header band ──────────────────────────────────────────────────────────
  const headH = 92;
  setFill(BRAND); doc.rect(0, 0, PW, headH, "F");
  // subtle second-tone wedge
  setFill(BRAND2); doc.triangle(PW, 0, PW, headH, PW - 200, 0, "F");
  if (logo) {
    // Full-color logo on a clean white plate for contrast over the brand band.
    const lw = 128, lh = lw * (logo.height / logo.width);
    const padX = 12, padY = 9;
    setFill([255, 255, 255]);
    doc.roundedRect(M, (headH - (lh + padY * 2)) / 2, lw + padX * 2, lh + padY * 2, 8, 8, "F");
    doc.addImage(logo.dataUrl, "PNG", M + padX, (headH - lh) / 2, lw, lh);
  } else {
    doc.setFont("helvetica", "bold"); doc.setFontSize(20); setText([255, 255, 255]);
    doc.text("TeamGrid", M, 52);
  }
  doc.setFont("helvetica", "bold"); doc.setFontSize(15); setText([255, 255, 255]);
  doc.text("Work Summary", PW - M, 44, { align: "right" });
  doc.setFont("helvetica", "normal"); doc.setFontSize(10); setText([235, 236, 250]);
  doc.text("AI Work Intelligence", PW - M, 62, { align: "right" });
  y = headH + 26;

  // ── Title block ──────────────────────────────────────────────────────────
  doc.setFont("helvetica", "bold"); doc.setFontSize(19); setText(INK.text);
  doc.text(memberName || "Team member", M, y);
  y += 18;
  doc.setFont("helvetica", "normal"); doc.setFontSize(10.5); setText(INK.sub);
  const metaLine = [position, dateLabel].filter(Boolean).join("  ·  ");
  if (metaLine) { doc.text(metaLine, M, y); y += 6; }
  doc.setFontSize(9); setText(INK.faint);
  doc.text(`Generated ${new Date().toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}`, M, y + 8);
  y += 22;

  // ── Headline callout ─────────────────────────────────────────────────────
  if (report?.headline) {
    doc.setFont("helvetica", "normal"); doc.setFontSize(11.5);
    const lines = doc.splitTextToSize(report.headline, CW - 28);
    const boxH = 20 + lines.length * 15;
    pageBreak(boxH);
    setFill([243, 243, 253]); setDraw([224, 224, 250]); doc.setLineWidth(1);
    doc.roundedRect(M, y, CW, boxH, 10, 10, "FD");
    setFill(BRAND); doc.roundedRect(M, y, 4, boxH, 2, 2, "F");
    setText([60, 60, 120]);
    doc.text(lines, M + 16, y + 20);
    y += boxH + 22;
  }

  // ── KPI strip ────────────────────────────────────────────────────────────
  if (metrics) {
    const cards = [
      { label: "TOTAL HOURS", value: fmtHours(metrics.totalHours), color: BRAND },
      { label: "SESSIONS", value: String(metrics.sessionCount ?? "—"), color: SKY },
      { label: "FOCUS", value: metrics.focusScore != null ? `${metrics.focusScore}%` : "—", color: GREEN },
      { label: metrics.breakdownKind === "app" ? "APPS USED" : "PROJECTS", value: String((metrics.projectBreakdown?.length) || "—"), color: [16, 185, 129] },
    ];
    const gap = 12, cardW = (CW - gap * 3) / 4, cardH = 58;
    pageBreak(cardH + 10);
    cards.forEach((c, i) => {
      const x = M + i * (cardW + gap);
      setFill([255, 255, 255]); setDraw(INK.line); doc.setLineWidth(1);
      doc.roundedRect(x, y, cardW, cardH, 9, 9, "FD");
      setFill(c.color); doc.roundedRect(x, y, cardW, 3, 1.5, 1.5, "F");
      doc.setFont("helvetica", "bold"); doc.setFontSize(7.5); setText(INK.faint);
      doc.text(c.label, x + 12, y + 20);
      doc.setFont("helvetica", "bold"); doc.setFontSize(17); setText(INK.text);
      doc.text(c.value, x + 12, y + 42);
    });
    y += cardH + 24;
  }

  // ── Section heading helper ───────────────────────────────────────────────
  const sectionHeading = (title, color) => {
    pageBreak(30);
    setFill(color); doc.roundedRect(M, y - 9, 3.5, 14, 1.5, 1.5, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(12.5); setText(INK.text);
    doc.text(title, M + 12, y + 2);
    y += 20;
  };

  // ── Activity breakdown ───────────────────────────────────────────────────
  if (report?.activityBreakdown?.length) {
    sectionHeading("How the time was spent", BRAND);
    const sorted = [...report.activityBreakdown].sort((a, b) => b.percentage - a.percentage);
    const barX = M + 150, barW = CW - 150 - 44;
    sorted.forEach((d) => {
      pageBreak(22);
      const col = catColor(d.category);
      doc.setFont("helvetica", "normal"); doc.setFontSize(10); setText(INK.sub);
      doc.text(String(d.category), M, y + 4);
      // track
      setFill([238, 240, 247]); doc.roundedRect(barX, y - 5, barW, 8, 4, 4, "F");
      // fill
      const w = Math.max(4, (d.percentage / 100) * barW);
      setFill(col); doc.roundedRect(barX, y - 5, w, 8, 4, 4, "F");
      doc.setFont("helvetica", "bold"); doc.setFontSize(9.5); setText(INK.text);
      doc.text(`${d.percentage}%`, PW - M, y + 4, { align: "right" });
      y += 20;
    });
    y += 8;
  }

  // ── Session timeline ─────────────────────────────────────────────────────
  if (report?.sessions?.length) {
    sectionHeading("Session-by-session", SKY);
    report.sessions.forEach((s, idx) => {
      // measure body
      doc.setFont("helvetica", "normal"); doc.setFontSize(10);
      const sumLines = s.summary ? doc.splitTextToSize(s.summary, CW - 46) : [];
      const acts = s.activities || [];
      const est = 40 + sumLines.length * 13 + acts.length * 24 + 16;
      pageBreak(est);

      const cardX = M, cardW = CW;
      const startY = y;
      // timeline node
      setFill(SKY); doc.circle(cardX + 8, y + 10, 4.5, "F");
      // card box
      setDraw(INK.line); doc.setLineWidth(1); setFill([255, 255, 255]);
      const bodyX = cardX + 24;
      // header
      doc.setFont("helvetica", "bold"); doc.setFontSize(11.5); setText(INK.text);
      doc.text(`${String(idx + 1).padStart(2, "0")}  ${s.title || `Session ${idx + 1}`}`, bodyX, y + 8);
      y += 20;
      // meta row
      doc.setFont("helvetica", "normal"); doc.setFontSize(9); setText(INK.faint);
      const meta = [s.timeRange, s.duration, s.project].filter(Boolean).join("   ·   ");
      doc.text(meta, bodyX, y);
      y += 14;
      // summary
      if (sumLines.length) {
        doc.setFontSize(10); setText(INK.sub);
        doc.text(sumLines, bodyX, y + 4); y += sumLines.length * 13 + 4;
      }
      // activities
      acts.forEach((a) => {
        pageBreak(22);
        const col = catColor(a.label);
        setFill(col); doc.circle(bodyX + 3, y + 1, 2.4, "F");
        doc.setFont("helvetica", "bold"); doc.setFontSize(9.5); setText(INK.text);
        doc.text(String(a.label || ""), bodyX + 12, y + 4);
        if (a.detail) {
          doc.setFont("helvetica", "normal"); doc.setFontSize(9.5); setText(INK.faint);
          const dLines = doc.splitTextToSize(String(a.detail), CW - 90);
          doc.text(dLines, bodyX + 12, y + 16);
          y += 16 + (dLines.length - 1) * 11;
        }
        y += 20;
      });
      // left rail connecting node to next
      setDraw([224, 232, 240]); doc.setLineWidth(1.5);
      doc.line(cardX + 8, startY + 16, cardX + 8, y - 4);
      y += 12;
    });
    y += 4;
  }

  // ── Highlights ───────────────────────────────────────────────────────────
  if (report?.highlights?.length) {
    sectionHeading("Key highlights", GREEN);
    report.highlights.forEach((h) => {
      doc.setFont("helvetica", "bold"); doc.setFontSize(10);
      const titleTxt = h.title ? `${h.title} — ` : "";
      doc.setFont("helvetica", "normal"); doc.setFontSize(10);
      const full = `${titleTxt}${h.text || ""}`;
      const lines = doc.splitTextToSize(full, CW - 20);
      pageBreak(lines.length * 13 + 8);
      setFill(GREEN); doc.circle(M + 3, y - 2, 2.6, "F");
      setText(INK.sub);
      // render title bold inline by drawing title then body — simpler: draw whole line, bold prefix separately
      if (h.title) {
        doc.setFont("helvetica", "bold"); setText(INK.text);
        const tW = doc.getTextWidth(h.title + "  ");
        doc.text(h.title, M + 14, y + 2);
        doc.setFont("helvetica", "normal"); setText(INK.sub);
        const rest = doc.splitTextToSize(h.text || "", CW - 14 - tW);
        doc.text(rest, M + 14 + tW, y + 2);
        y += Math.max(1, rest.length) * 13 + 8;
      } else {
        doc.text(lines, M + 14, y + 2);
        y += lines.length * 13 + 8;
      }
    });
    y += 6;
  }

  // ── Insights ─────────────────────────────────────────────────────────────
  if (report?.insights?.length) {
    sectionHeading("Insights & recommendations", AMBER);
    report.insights.forEach((ins) => {
      const col = ins.type === "positive" ? GREEN : ins.type === "mixed" ? SKY : AMBER;
      const tag = ins.type === "positive" ? "STRENGTH" : ins.type === "mixed" ? "NOTE" : "OPPORTUNITY";
      doc.setFont("helvetica", "normal"); doc.setFontSize(10);
      const lines = doc.splitTextToSize(ins.text || "", CW - 28);
      const boxH = 16 + (ins.title ? 14 : 0) + lines.length * 13 + 10;
      pageBreak(boxH);
      const bg = ins.type === "positive" ? [240, 249, 243] : ins.type === "mixed" ? [239, 249, 253] : [253, 246, 235];
      setFill(bg); setDraw([col[0], col[1], col[2]]); doc.setLineWidth(0.8);
      doc.roundedRect(M, y, CW, boxH, 9, 9, "FD");
      doc.setFont("helvetica", "bold"); doc.setFontSize(7.5); setText(col);
      doc.text(tag + (ins.title ? `   ·   ${ins.title}` : ""), M + 14, y + 16);
      doc.setFont("helvetica", "normal"); doc.setFontSize(10); setText(INK.sub);
      doc.text(lines, M + 14, y + 16 + 15);
      y += boxH + 12;
    });
  }

  // ── Footer on every page ─────────────────────────────────────────────────
  function footer() {
    const p = doc.internal.getCurrentPageInfo().pageNumber;
    setDraw(INK.line); doc.setLineWidth(0.8);
    doc.line(M, PH - 40, PW - M, PH - 40);
    doc.setFont("helvetica", "normal"); doc.setFontSize(8); setText(INK.faint);
    doc.text("Confidential · Generated by TeamGrid Work Intelligence", M, PH - 26);
    doc.text(`Page ${p}`, PW - M, PH - 26, { align: "right" });
  }
  footer();

  const safe = (memberName || "member").replace(/[^\w]+/g, "_");
  doc.save(`WorkSummary_${safe}_${(dateLabel || "").replace(/[^\w]+/g, "_")}.pdf`);
}

export default downloadWorkSummaryPDF;
